import React, { useState } from 'react';
import { Database, RefreshCw, Loader, Check, AlertTriangle } from 'lucide-react';
import { calculateSubnetMindshare } from '../utils/mindshareCalculator';
import { saveTensorpulseMindshare, updateLastUpdateTimestamp } from '../services/tensorpulseMindshareService';
import { updateAllCurrentMindshare } from '../services/currentMindshareService';
import { clearAllCaches } from '../utils/cacheUtils';

export default function DevPage() {
  const [isCalculating, setIsCalculating] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const [updatedCount, setUpdatedCount] = useState(0);
  const [forceHistoryUpdate, setForceHistoryUpdate] = useState(false);

  const handleRecalculateMindshare = async () => {
    try {
      setIsCalculating(true);
      setStatus('idle');
      setMessage('Clearing caches...');
      
      // Clear all caches first
      clearAllCaches();
      
      setMessage('Calculating mindshare data...');
      
      // Calculate mindshare data
      const mindshareData = await calculateSubnetMindshare();
      
      if (mindshareData.length === 0) {
        throw new Error('No mindshare data was calculated');
      }
      
      setMessage(`Saving ${mindshareData.length} subnet mindshare entries...`);
      
      // Save to TensorPulse mindshare collection
      const tensorPulseSavePromises = mindshareData.map(data => 
        saveTensorpulseMindshare({
          ...data,
          _forceHistoryUpdate: forceHistoryUpdate
        })
      );
      
      await Promise.all(tensorPulseSavePromises);
      
      // Update the last update timestamp
      await updateLastUpdateTimestamp();
      
      setMessage('Updating current mindshare collection...');
      
      // Update current mindshare collection
      const updatedCurrentCount = await updateAllCurrentMindshare();
      setUpdatedCount(updatedCurrentCount);
      
      setStatus('success');
      setMessage(`Successfully recalculated mindshare for ${mindshareData.length} subnets and updated ${updatedCurrentCount} entries in current_mindshare collection.`);
    } catch (error) {
      console.error('Error recalculating mindshare:', error);
      setStatus('error');
      setMessage(error instanceof Error ? error.message : 'An unknown error occurred');
    } finally {
      setIsCalculating(false);
    }
  };

  return (
    <div className="bg-black/40 backdrop-blur-sm rounded-xl shadow-2xl p-4 md:p-8 border border-white/10">
      <div className="flex items-center space-x-3 mb-6">
        <Database className="w-8 h-8 text-cyan-400" />
        <h1 className="text-2xl md:text-3xl font-bold">Developer Tools</h1>
      </div>

      <div className="space-y-6">
        <div className="bg-black/20 backdrop-blur-sm rounded-lg p-6 border border-white/5">
          <h2 className="text-xl font-bold mb-4">Mindshare Recalculation</h2>
          
          <div className="space-y-4">
            <p className="text-gray-300">
              This tool allows you to manually recalculate the mindshare data for all subnets. 
              This will update both the tensorpulse_mindshare and current_mindshare collections.
            </p>
            
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="forceHistoryUpdate"
                checked={forceHistoryUpdate}
                onChange={(e) => setForceHistoryUpdate(e.target.checked)}
                className="rounded bg-black/20 border-white/20 text-cyan-400 focus:ring-cyan-400"
              />
              <label htmlFor="forceHistoryUpdate" className="text-sm text-gray-300">
                Force history update (creates new history entries regardless of time since last update)
              </label>
            </div>
            
            <button
              onClick={handleRecalculateMindshare}
              disabled={isCalculating}
              className={`px-4 py-2 rounded-lg flex items-center space-x-2 ${
                isCalculating 
                  ? 'bg-gray-600 cursor-not-allowed' 
                  : 'bg-cyan-600 hover:bg-cyan-700'
              }`}
            >
              {isCalculating ? (
                <Loader className="w-5 h-5 animate-spin" />
              ) : (
                <RefreshCw className="w-5 h-5" />
              )}
              <span>{isCalculating ? 'Recalculating...' : 'Recalculate Mindshare'}</span>
            </button>
            
            {status !== 'idle' && (
              <div className={`mt-4 p-3 rounded-lg flex items-start space-x-2 ${
                status === 'success' ? 'bg-green-900/30 text-green-400' : 'bg-red-900/30 text-red-400'
              }`}>
                {status === 'success' ? (
                  <Check className="w-5 h-5 mt-0.5 flex-shrink-0" />
                ) : (
                  <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                )}
                <span>{message}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}